// useUnifiedRegen — unified regen entry point for embedded ItemActionMenu.
//
// 19BJ Session 2: Regen embedded architecture migration.
// 19BJ Session 5: Quick path timeout + downgrade (Audit B1.2) + forceDeep.
// Design doc: docs/architecture/regen-embedded-design.md §2.3.
//
// Responsibilities:
//   1. Auto-route between Quick (sync POST /ingest/quick-regen) and
//      Deep (async POST /ingest/regenerate + poll) based on scope size.
//      `forceDeep` opts out of Quick entirely (chapter-scoped regen, which
//      is expected to exceed the 30s sync budget — used by ChapterRegenButton).
//   2. Manage local preview state (items, cost, error) independent of
//      the global regenSlice (which still serves the Regen Center).
//   3. Expose accept() / reject() for the RegenPreview UI.
//
// Relationship with existing hooks:
//   - useRegenAction (startPartialRegen) → Regen Center Batch tab (unchanged)
//   - useRegenFlow (confirmRegen/discardRegen) → Regen Center confirm flow (unchanged)
//   - useUnifiedRegen → embedded ItemActionMenu in content views (new)
//
// Quick path:  await postQuickRegen() → setState(preview).
//   Audit B1.2: if the sync call exceeds QUICK_REGEN_TIMEOUT_MS, downgrade to
//   the Deep path instead of surfacing a hard failure. Users see spinner →
//   progress bar (R2: they don't choose Quick/Deep, but the path is exposed
//   so UI can differentiate feedback — N3).
// Deep path:   await postRegenerate() → write regenSlice → CourseShell polls
//              → regenSlice status='confirm' → setState(preview).
//
// Staleness control (Session 5): a generation token (genRef) is bumped on every
// trigger()/reset(). Async callbacks check it before setState so a stale Quick
// result (e.g. one that raced with a timeout-downgrade, or a re-trigger) cannot
// clobber the current generation's state. This replaces the prior abortRef,
// which was dead weight — postV4's fetch carries no AbortSignal, so abort()
// could not actually cancel the in-flight request.

import { useCallback, useRef, useState } from 'react';
import { useStore } from '../store/index.js';
import { useStoreCourseData } from './use-store-course.js';
import { useEventBus } from './use-event-bus.js';
import { postQuickRegen, postRegenerate, confirmRegenerate } from '../../api/ingest.js';
import { mergeStageRegenerationResult } from '../../storage/idb.js';
import { shouldQuickRegen } from '../regen-utils.js';
import { reportBehaviorError } from '../report-behavior-error.js';
// 19BN Phase 0: send real anonId instead of 'web-client' literal.
import { getOrCreateAnonId } from '../../auth/anon-id.js';
import type { PartialRegenStage } from '../../storage/idb.js';
import type { RegenAction } from '@examforge/shared/schemas';

// ── Types ──────────────────────────────────────────────────────────────────────

export type UnifiedRegenStatus = 'idle' | 'loading' | 'preview' | 'done' | 'failed';

export interface UnifiedRegenState {
  status: UnifiedRegenStatus;
  /** Which path was selected — exposed so UI can differentiate feedback.
   *  Audit N3: 'quick' shows spinner, 'deep' shows progress bar. */
  path: 'quick' | 'deep' | null;
  /** Items proposed by the regen, awaiting user confirmation. */
  previewItems: unknown[];
  /** Snapshot of original items before regen (for diff display). */
  originalItems: unknown[];
  /** The RegenAction that was triggered. */
  action: RegenAction | null;
  /** v4 regenId — used for confirm/reject Phase 2 call. */
  regenId: string | null;
  /** Stage being regenerated. */
  stage: string;
  /** Chapter ID, if scoped to a chapter. */
  chapterId: string | null;
  /** Progress info for Deep path (from regenSlice polling). */
  progress: { current: number; total: number; text: string } | null;
  /** Last error message. */
  error: string | null;
  /** Token cost info (Quick path returns it synchronously). */
  cost: { tokensIn: number; tokensOut: number; costRmbEstimate: number } | null;
}

/** Optional trigger overrides. */
export interface TriggerOpts {
  /** Skip the Quick path entirely and go straight to Deep. Use when the
   *  scope is expected to exceed the 30s sync budget (chapter-scoped regen). */
  forceDeep?: boolean;
}

const INITIAL_STATE: UnifiedRegenState = {
  status: 'idle',
  path: null,
  previewItems: [],
  originalItems: [],
  action: null,
  regenId: null,
  stage: 'mcqs',
  chapterId: null,
  progress: null,
  error: null,
  cost: null,
};

/** Timeout for Quick Regen sync HTTP call. After this, the call is abandoned
 *  (the in-flight fetch keeps running in the background but its result is
 *  ignored) and the action is retried on the Deep path.
 *
 *  postV4 does not thread an AbortSignal into fetch, so we cannot truly cancel
 *  the request — Promise.race lets us stop *waiting* for it. */
const QUICK_REGEN_TIMEOUT_MS = 30_000;

/** Error sentinel thrown by triggerQuick when the sync call exceeds the
 *  timeout. trigger() catches this specific type and downgrades to the Deep
 *  path (Audit B1.2). Distinguished from real network errors so they still
 *  surface as failures. */
class QuickTimeoutError extends Error {
  override readonly name = 'QuickTimeoutError';
  constructor() {
    super('Quick regen exceeded 30s — downgrading to async');
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Extract stage from a RegenAction scope. Uses bracket notation because
 *  RegenAction is a discriminated union and TS doesn't narrow scope fields. */
function getScopeStage(action: RegenAction): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (action as Record<string, any>)['scope']?.['stage'] as string | undefined;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useUnifiedRegen(courseId: string) {
  const [state, setState] = useState<UnifiedRegenState>(INITIAL_STATE);
  const { refreshProgress } = useStoreCourseData();
  const eventBus = useEventBus();
  const setRegenState = useStore((s) => s.setRegenState);
  const resetRegen = useStore((s) => s.resetRegen);
  // 19BN Phase 2: read balance for preflight checks (selector · test-mockable).
  const balance = useStore((s) => s.balance);

  // Track mounted state to avoid setState after unmount.
  const mountedRef = useRef(true);
  // Generation token — bumped on every trigger()/reset(). Async callbacks
  // compare it before setState so stale results can't clobber current state.
  const genRef = useRef(0);

  // ── triggerDeep (Deep path: async workflow + regenSlice polling) ──────────
  //
  // Writes to the global regenSlice so CourseShell's poll loop picks it up.
  // On completion the poll loop transitions regenSlice to 'confirm' and
  // ItemActionMenu calls loadPreviewFromRegenSlice() to surface items here.

  const triggerDeep = useCallback(async (
    action: RegenAction,
    resolvedStage: string,
    resolvedChapterId: string | null,
    gen: number,
  ): Promise<void> => {
    // 19BN Phase 2: preflight credit check before deep regen (~26 points).
    if (balance !== null && balance !== undefined && balance < 26) return;

    const result = await postRegenerate({ courseId, action });

    if (gen !== genRef.current || !mountedRef.current) return;

    setRegenState({
      regenStatus: 'running',
      regenRunId: result.runId,
      regenId: result.regenId,
      regenStartedAt: Date.now(),
      regenTargetStage: resolvedStage,
      regenTargetChapter: resolvedChapterId,
      regenMode: (action.kind === 'add' ? 'add' : 'replace'),
      regenProgress: 'Submitting…',
    });

    setState((s) => ({
      ...s,
      path: 'deep',
      regenId: result.regenId,
    }));
    // deps include `balance` (not just courseId/setRegenState) because the
    // preflight credit check on the next line reads it — must re-evaluate
    // when balance changes (recharge/spend). balance is a numeric store
    // value updated only on user action, so no render loop.
  }, [courseId, setRegenState, balance]);

  // ── triggerQuick (Quick path: sync POST /ingest/quick-regen) ──────────────
  //
  // Race the sync call against a timeout. On timeout, throw QuickTimeoutError
  // so trigger() can downgrade to Deep. Real errors propagate unchanged.

  const triggerQuick = useCallback(async (
    action: RegenAction,
    _resolvedStage: string,
    _resolvedChapterId: string | null,
    gen: number,
  ): Promise<void> => {
    let timedOut = false;
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        timedOut = true;
        reject(new QuickTimeoutError());
      }, QUICK_REGEN_TIMEOUT_MS);
    });

    // 19BN Phase 2: preflight credit check before quick-regen (~6 points).
    if (balance !== null && balance !== undefined && balance < 6) return;

    let result: Awaited<ReturnType<typeof postQuickRegen>>;
    try {
      result = await Promise.race([
        postQuickRegen({ courseId, anonId: getOrCreateAnonId(), action }),
        timeoutPromise,
      ]);
    } catch (err) {
      // Distinguish timeout (→ downgrade) from a real network/schema error
      // (→ surface as failure). A real error must not be masked as a timeout.
      if (timedOut) throw new QuickTimeoutError();
      throw err;
    }

    if (gen !== genRef.current || !mountedRef.current) return;

    setState((s) => ({
      ...s,
      status: 'preview',
      path: 'quick',
      previewItems: result.items,
      originalItems: result.beforeSnapshot,
      regenId: result.regenId,
      cost: {
        tokensIn: result.cost.tokensIn,
        tokensOut: result.cost.tokensOut,
        costRmbEstimate: result.cost.costRmbEstimate,
      },
    }));
    // deps include `balance` (not just courseId) because the preflight credit
    // check above reads it — must re-evaluate when balance changes.
  }, [courseId, balance]);

  // ── trigger ────────────────────────────────────────────────────────────────

  const trigger = useCallback(async (
    action: RegenAction,
    stage?: string,
    chapterId?: string,
    opts?: TriggerOpts,
  ): Promise<void> => {
    const gen = ++genRef.current;
    const resolvedStage = stage ?? getScopeStage(action) ?? 'mcqs';
    const resolvedChapterId = chapterId ?? null;

    setState((s) => ({
      ...s,
      status: 'loading',
      path: null,
      error: null,
      action,
      stage: resolvedStage,
      chapterId: resolvedChapterId,
      previewItems: [],
      originalItems: [],
      cost: null,
    }));

    const goDeep = opts?.forceDeep === true || !shouldQuickRegen(action);

    try {
      if (goDeep) {
        await triggerDeep(action, resolvedStage, resolvedChapterId, gen);
      } else {
        await triggerQuick(action, resolvedStage, resolvedChapterId, gen);
      }
    } catch (err) {
      if (gen !== genRef.current || !mountedRef.current) return;

      // Audit B1.2: Quick timeout → downgrade to Deep instead of hard failure.
      if (err instanceof QuickTimeoutError) {
        setState((s) => ({ ...s, path: 'deep' }));
        try {
          await triggerDeep(action, resolvedStage, resolvedChapterId, gen);
        } catch (deepErr) {
          if (gen !== genRef.current || !mountedRef.current) return;
          setState((s) => ({
            ...s,
            status: 'failed',
            error: deepErr instanceof Error ? deepErr.message : String(deepErr),
          }));
        }
        return;
      }

      const msg = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, status: 'failed', error: msg }));
    }
  }, [triggerDeep, triggerQuick]);

  // ── loadPreviewFromRegenSlice ──────────────────────────────────────────────
  //
  // Called by ItemActionMenu when the global regenSlice transitions to
  // 'confirm' (Deep path complete). Copies the pending items into local state
  // so RegenPreview can render them.

  const loadPreviewFromRegenSlice = useCallback((
    pendingItems: unknown[],
    beforeSnapshot: unknown[],
    _diffSummary: string | null,
    regenId: string,
  ): void => {
    setState((s) => ({
      ...s,
      status: 'preview',
      previewItems: pendingItems,
      originalItems: beforeSnapshot,
      regenId,
    }));
  }, []);

  // ── accept ─────────────────────────────────────────────────────────────────

  const accept = useCallback(async (selectedItems?: unknown[]): Promise<void> => {
    const items = selectedItems ?? state.previewItems;
    if (items.length === 0) {
      setState((s) => ({ ...s, status: 'done' }));
      return;
    }

    try {
      await mergeStageRegenerationResult(
        courseId,
        state.stage as PartialRegenStage,
        items,
        state.action?.kind === 'add' ? 'add' : 'replace',
        state.chapterId ?? undefined,
      );
    } catch (err) {
      // 19BM Work-area A: report the swallowed IDB merge failure so the operator
      // gains visibility. Best-effort: even if IDB merge fails, try to confirm on
      // backend — but now the failure reaches the admin feed instead of vanishing
      // into console.warn (UI showed success while local state may have desynced).
      const viewPath = typeof location !== 'undefined' ? location.pathname : '';
      void reportBehaviorError({
        viewPath,
        userAction: '内容生成 IDB merge',
        code: 'regen-idb-merge',
      });
      // eslint-disable-next-line no-console
      console.warn('[useUnifiedRegen] IDB merge failed, continuing:', err);
    }

    // Best-effort backend confirm.
    if (state.regenId) {
      try {
        await confirmRegenerate({ regenId: state.regenId, decision: 'apply' });
      } catch {
        // Backend may not be reachable — local merge is what matters.
      }
    }

    await refreshProgress();
    eventBus.emit('ingest:completed', { courseId, success: true, itemCount: items.length });

    setState((s) => ({ ...s, status: 'done', previewItems: [], action: null }));
  }, [courseId, state.stage, state.action, state.chapterId, state.regenId,
      state.previewItems, refreshProgress, eventBus]);

  // ── reject ─────────────────────────────────────────────────────────────────

  const reject = useCallback((): void => {
    // Best-effort backend reject.
    if (state.regenId) {
      confirmRegenerate({ regenId: state.regenId, decision: 'reject' }).catch(() => {
        // Best-effort — local state is what matters.
      });
    }

    // Clear global regenSlice if this was a Deep regen.
    if (state.path === 'deep') {
      resetRegen();
    }

    setState((s) => ({ ...s, status: 'idle', previewItems: [], action: null, regenId: null }));
  }, [state.regenId, state.path, resetRegen]);

  // ── reset ──────────────────────────────────────────────────────────────────

  const reset = useCallback((): void => {
    genRef.current++; // invalidate any in-flight trigger
    resetRegen();
    setState(INITIAL_STATE);
  }, [resetRegen]);

  return {
    ...state,
    trigger,
    loadPreviewFromRegenSlice,
    accept,
    reject,
    reset,
  };
}
